import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/auth';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { MedicationLogStatus, type DashboardData, type TaskStatus } from '@kinapp/shared';
import { CheckInCard } from '../components/CheckInCard';
import { Modal } from '../components/Modal';
import { CreateTaskForm } from '../components/CreateTaskForm';
import { CreateMedicationForm } from '../components/CreateMedicationForm';
import { CreateAppointmentForm } from '../components/CreateAppointmentForm';
import { CreateDiaryEntryForm } from '../components/CreateDiaryEntryForm';
import { AppointmentMap } from '../components/AppointmentMap';
import { ActivityFeed } from '../components/ActivityFeed';
import { CustodyCard } from '../components/CustodyCard';
import { CustodyCalendar } from '../components/CustodyCalendar';
import { CustodySetup } from '../components/CustodySetup';
import { HandoverLog } from '../components/HandoverLog';
import { ActivityCard } from '../components/ActivityCard';
import { ActivityForm } from '../components/ActivityForm';
import { WeeklySchedule } from '../components/WeeklySchedule';
import { DependentChips } from '../components/DependentChips';
import { DependentBadge } from '../components/DependentBadge';

// Staff dashboard data shape (returned by api.getStaffDashboard)
interface StaffDependentData {
  dependent: { id: string; name: string };
  isScheduled: boolean;
  isPrimary: boolean;
  todaysTasks: any[];
  todaysMedications: { medication: any; logs: any[] }[];
  upcomingAppointments: any[];
}
interface StaffDashboardData {
  careHome: { id: string; name: string };
  staff: { id: string; name?: string; email: string; isAdmin: boolean };
  dependents: StaffDependentData[];
  mySchedule: any[];
  internalTasks: any[];
}

export function DashboardPage() {
  const { user } = useAuth();
  const { activeWorkspace, isPro, loading: workspaceLoading } = useWorkspace();

  // Family workspace state
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [activeDepIds, setActiveDepIds] = useState<string[]>([]);

  // Pro (care home) workspace state
  const [staffDashboard, setStaffDashboard] = useState<StaffDashboardData | null>(null);
  const [showAllResidents, setShowAllResidents] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showCreateMedication, setShowCreateMedication] = useState(false);
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [showCreateDiaryEntry, setShowCreateDiaryEntry] = useState(false);
  const [showCustodySetup, setShowCustodySetup] = useState(false);
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [activityKey, setActivityKey] = useState(0);

  useEffect(() => {
    if (!workspaceLoading && activeWorkspace) {
      loadDashboard();
    }
  }, [activeDepIds, activeWorkspace?.id, isPro, showAllResidents, workspaceLoading]);

  const loadDashboard = async () => {
    if (!activeWorkspace) return;
    try {
      if (isPro) {
        const data = await api.getStaffDashboard(activeWorkspace.id, { showAll: showAllResidents });
        setStaffDashboard(data);
      } else {
        const data = await api.getDashboard(activeWorkspace.id, activeDepIds.length ? activeDepIds : undefined);
        setDashboard(data);
        // Initialize activeDepIds to all dependents on first load (multi-dep only)
        if (!activeDepIds.length && data.dependents?.length > 1) {
          setActiveDepIds(data.dependents.map((d: any) => d.id));
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkTaskDone = async (taskId: string) => {
    try {
      await api.updateTask(taskId, { status: 'DONE' as TaskStatus });
      await loadDashboard();
    } catch (err: any) {
      alert('Failed to update task: ' + err.message);
    }
  };

  const handleLogMedication = async (medicationId: string, status: MedicationLogStatus) => {
    try {
      await api.logMedication({
        medicationId,
        status,
        takenAt: new Date().toISOString(),
      });
      await loadDashboard();
    } catch (err: any) {
      alert('Failed to log medication: ' + err.message);
    }
  };

  const formatTime = (date: string | Date) => {
    return new Date(date).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (date: string | Date) => {
    return new Date(date).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper: get color index for a dependent by ID
  const depColorIndex = (depId: string) => {
    if (!dashboard) return 0;
    return dashboard.dependents.findIndex((d) => d.id === depId);
  };

  // Primary dependent for per-dependent features (custody calendar, handover log)
  const primaryDepId = activeDepIds[0] || dashboard?.dependents[0]?.id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  // ── Pro (Care Home) Dashboard ─────────────────────────────────────────────
  if (isPro) {
    if (!staffDashboard && !loading) return null;
    if (!staffDashboard) return null;
    return (
      <div className="min-h-full bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">{staffDashboard.careHome.name}</h1>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showAllResidents}
                onChange={(e) => setShowAllResidents(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Show all residents
            </label>
          </div>
        </div>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {/* My Schedule */}
          {staffDashboard.mySchedule.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Today's Schedule</h2>
              <div className="flex flex-wrap gap-3">
                {staffDashboard.mySchedule.map((schedule: any) => (
                  <div
                    key={schedule.id}
                    className={`px-4 py-2 rounded-lg border ${schedule.isPrimary ? 'bg-primary-50 border-primary-200' : 'bg-white border-gray-200'}`}
                  >
                    <div className="font-medium text-gray-900">{schedule.dependent.name}</div>
                    <div className="text-sm text-gray-600">
                      {schedule.startTime} – {schedule.endTime}
                      {schedule.isPrimary && <span className="ml-2 text-primary-600">(Primary)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {/* Internal Tasks */}
          {staffDashboard.internalTasks.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Internal Tasks</h2>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                {staffDashboard.internalTasks.map((task: any) => (
                  <div key={task.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{task.title}</div>
                      {task.description && <div className="text-sm text-gray-500">{task.description}</div>}
                      {task.dueAt && (
                        <div className="text-xs text-gray-400">Due: {new Date(task.dueAt).toLocaleDateString()}</div>
                      )}
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Staff only</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {/* Residents Grid */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Residents</h2>
            {staffDashboard.dependents.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {staffDashboard.dependents.map(({ dependent, isScheduled, isPrimary, todaysTasks, todaysMedications, upcomingAppointments }) => (
                  <div
                    key={dependent.id}
                    className={`bg-white rounded-xl border overflow-hidden ${isPrimary ? 'ring-2 ring-primary-400 border-primary-200' : 'border-gray-200'}`}
                  >
                    <div className={`px-4 py-3 flex items-center justify-between ${isPrimary ? 'bg-primary-50' : 'bg-gray-50'}`}>
                      <h3 className="font-semibold text-gray-900">{dependent.name}</h3>
                      {isScheduled && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPrimary ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-600'}`}>
                          {isPrimary ? 'Primary' : 'Scheduled'}
                        </span>
                      )}
                    </div>
                    <div className="p-4 space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Tasks ({todaysTasks.length})</p>
                        {todaysTasks.length > 0 ? (
                          <ul className="space-y-1">
                            {todaysTasks.slice(0, 3).map((task: any) => (
                              <li key={task.id} className="flex items-center justify-between text-sm">
                                <span className={task.status === 'DONE' ? 'line-through text-gray-400' : 'text-gray-700'}>{task.title}</span>
                                {task.status !== 'DONE' && (
                                  <button
                                    onClick={async () => { await api.updateTask(task.id, { status: 'DONE' }); loadDashboard(); }}
                                    className="text-xs text-primary-600 hover:text-primary-800 ml-2"
                                  >
                                    Done
                                  </button>
                                )}
                              </li>
                            ))}
                            {todaysTasks.length > 3 && <li className="text-xs text-gray-400">+{todaysTasks.length - 3} more</li>}
                          </ul>
                        ) : <p className="text-sm text-gray-400">No tasks today</p>}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Medications ({todaysMedications.length})</p>
                        {todaysMedications.length > 0 ? (
                          <ul className="space-y-1">
                            {todaysMedications.slice(0, 3).map(({ medication, logs }: any) => (
                              <li key={medication.id} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{medication.name}</span>
                                <span className="text-xs text-gray-400">{logs.length}/{medication.timesPerDay} logged</span>
                              </li>
                            ))}
                          </ul>
                        ) : <p className="text-sm text-gray-400">No medications today</p>}
                      </div>
                      {upcomingAppointments.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Upcoming</p>
                          <ul className="space-y-0.5">
                            {upcomingAppointments.slice(0, 2).map((apt: any) => (
                              <li key={apt.id} className="text-sm text-gray-600">{apt.title} – {new Date(apt.dateTime).toLocaleDateString()}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
                No residents to display
              </div>
            )}
          </section>
        </main>
      </div>
    );
  }

  // ── Family Dashboard ─────────────────────────────────────────────────────────
  if (!dashboard) {
    return null;
  }

  const effectiveActiveIds = new Set(activeDepIds.length ? activeDepIds : dashboard.dependents.map((d) => d.id));
  const hasCustody = dashboard.todayCustody && dashboard.todayCustody.length > 0;

  return (
    <div className="min-h-full bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-xl font-bold text-gray-900">{dashboard.family.name}</h1>
        {dashboard.dependents.length > 1 && (
          <DependentChips
            dependents={dashboard.dependents}
            activeIds={effectiveActiveIds}
            onToggle={(id) => {
              const current = new Set(effectiveActiveIds);
              if (current.has(id) && current.size > 1) {
                current.delete(id);
              } else {
                current.add(id);
              }
              setActiveDepIds(Array.from(current));
            }}
          />
        )}
      </div>

      {/* Dashboard Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Tasks */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Tasks</h2>
              <button
                onClick={() => setShowCreateTask(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add task
              </button>
            </div>
            {dashboard.todaysTasks.length === 0 ? (
              <p className="text-gray-500 text-sm">No tasks for today</p>
            ) : (
              <div className="space-y-3">
                {dashboard.todaysTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`p-4 rounded-lg border ${
                      task.status === 'OVERDUE'
                        ? 'border-red-200 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {(task as any).dependent && (
                            <DependentBadge
                              name={(task as any).dependent.name}
                              colorIndex={depColorIndex((task as any).dependent.id)}
                            />
                          )}
                          <h3 className="font-medium text-gray-900">{task.title}</h3>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        )}
                        <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                          {(task as any).assignedTo && <span>Assigned to: {(task as any).assignedTo.name}</span>}
                          {task.dueAt && (
                            <span>
                              Due: {formatDateTime(task.dueAt)}
                              {task.status === 'OVERDUE' && (
                                <span className="ml-1 text-red-600 font-medium">OVERDUE</span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      {task.status !== 'DONE' && (
                        <button
                          onClick={() => handleMarkTaskDone(task.id)}
                          className="ml-4 px-3 py-1 text-sm btn btn-primary"
                        >
                          Mark done
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Today's Medications (only if feature enabled) */}
          {dashboard.family.hasMedications && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Medications</h2>
              <button
                onClick={() => setShowCreateMedication(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add medication
              </button>
            </div>
            {dashboard.todaysMedications.length === 0 ? (
              <p className="text-gray-500 text-sm">No medications scheduled</p>
            ) : (
              <div className="space-y-3">
                {dashboard.todaysMedications.map(({ medication, logs }) => {
                  const hasTakenToday = logs.some((log) => log.status === 'TAKEN');
                  return (
                    <div key={medication.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {(medication as any).dependent && (
                              <DependentBadge
                                name={(medication as any).dependent.name}
                                colorIndex={depColorIndex((medication as any).dependent.id)}
                              />
                            )}
                            <h3 className="font-medium text-gray-900">
                              {medication.name}
                              {medication.dose && (
                                <span className="ml-2 text-sm text-gray-600">({medication.dose})</span>
                              )}
                            </h3>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {medication.timesPerDay}x per day
                            {medication.assignedToId && (
                              <span className="ml-2">
                                • Assigned to:{' '}
                                {
                                  dashboard.familyMembers.find(
                                    (m) => m.user.id === medication.assignedToId
                                  )?.user.name
                                }
                              </span>
                            )}
                          </div>
                          {logs.length > 0 && (
                            <div className="mt-2 text-xs text-gray-600">
                              Last logged: {formatTime(logs[0].takenAt)} -{' '}
                              <span
                                className={
                                  logs[0].status === 'TAKEN'
                                    ? 'text-green-600'
                                    : 'text-yellow-600'
                                }
                              >
                                {logs[0].status}
                              </span>
                            </div>
                          )}
                        </div>
                        {!hasTakenToday && (
                          <div className="ml-4 flex space-x-2">
                            <button
                              onClick={() => handleLogMedication(medication.id, MedicationLogStatus.TAKEN)}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Taken
                            </button>
                            <button
                              onClick={() => handleLogMedication(medication.id, MedicationLogStatus.MISSED)}
                              className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                            >
                              Missed
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Custody (only if feature enabled) */}
          {dashboard.family.hasCustody && (
            hasCustody ? (
              <>
                {dashboard.todayCustody!.map((custody) => (
                  <CustodyCard key={custody.dependentId} custody={custody} />
                ))}
              </>
            ) : (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Custody</h2>
                <p className="text-gray-500 text-sm mb-3">No custody schedule set up yet.</p>
                <button
                  onClick={() => setShowCustodySetup(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  + Set up custody schedule
                </button>
              </div>
            )
          )}

          {/* Custody Calendar & Handovers */}
          {dashboard.family.hasCustody && hasCustody && primaryDepId && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Custody Calendar</h2>
                <button
                  onClick={() => setShowCustodySetup(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Edit schedule
                </button>
              </div>
              <CustodyCalendar
                familyId={dashboard.family.id}
                dependentId={primaryDepId}
                familyMembers={dashboard.familyMembers}
              />
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Handovers</h3>
                <HandoverLog
                  familyId={dashboard.family.id}
                  dependentId={primaryDepId}
                  currentUserId={user?.id}
                  onUpdate={loadDashboard}
                />
              </div>
            </div>
          )}

          {/* Daily Check-ins */}
          {(dashboard.dependents.filter((d) => activeDepIds.length === 0 || activeDepIds.includes(d.id))).map((dep) => (
            <CheckInCard
              key={dep.id}
              dependentName={dep.name}
              dependentId={dep.id}
              familyId={dashboard.family.id}
              checkIn={(dashboard.todayCheckIns ?? []).find((c) => c.dependentId === dep.id) ?? null}
              recentCheckIns={dashboard.recentCheckIns ?? []}
              onSaved={loadDashboard}
            />
          ))}

          {/* Upcoming Appointments */}
          <div className="card lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Appointments</h2>
              <button
                onClick={() => setShowCreateAppointment(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add appointment
              </button>
            </div>
            {dashboard.upcomingAppointments.length === 0 ? (
              <p className="text-gray-500 text-sm">No upcoming appointments</p>
            ) : (
              <div className="space-y-3">
                {dashboard.upcomingAppointments.map((appointment) => (
                  <div key={appointment.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {(appointment as any).dependent && (
                            <DependentBadge
                              name={(appointment as any).dependent.name}
                              colorIndex={depColorIndex((appointment as any).dependent.id)}
                            />
                          )}
                          <h3 className="font-medium text-gray-900">{appointment.title}</h3>
                        </div>
                        <div className="mt-1 text-sm text-gray-600 space-y-1">
                          <div>{formatDateTime(appointment.dateTime)}</div>

                          {appointment.locationLat && appointment.locationLng && appointment.location ? (
                            <div className="mt-3 mb-2">
                              <AppointmentMap
                                lat={appointment.locationLat}
                                lng={appointment.locationLng}
                                locationName={appointment.location}
                                className="h-48 w-full lg:w-80"
                              />
                              <div className="mt-1 text-xs text-gray-500">{appointment.location}</div>
                            </div>
                          ) : appointment.location ? (
                            <div>Location: {appointment.location}</div>
                          ) : null}

                          {(appointment as any).attendingUser && (
                            <div>Attending: {(appointment as any).attendingUser.name}</div>
                          )}
                          {appointment.notes && (
                            <div className="text-gray-500">{appointment.notes}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Today's Activities (only if feature enabled) */}
          {dashboard.family.hasActivities && dashboard.todaysActivities && (
            <ActivityCard activities={dashboard.todaysActivities} />
          )}

          {/* Weekly Schedule (only if feature enabled) */}
          {dashboard.family.hasActivities && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Weekly Schedule</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCreateActivity(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    + Add activity
                  </button>
                  <Link to="/calendar" className="text-sm text-gray-500 hover:text-gray-900 font-medium">
                    Full calendar →
                  </Link>
                </div>
              </div>
              <WeeklySchedule
                familyId={dashboard.family.id}
                dependentIds={activeDepIds.length ? activeDepIds : undefined}
              />
            </div>
          )}

          {/* Recent Activity */}
          <div className="card lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <button
                onClick={() => setShowCreateDiaryEntry(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add diary entry
              </button>
            </div>
            <ActivityFeed
              key={activityKey}
              familyId={dashboard.family.id}
              dependentIds={activeDepIds.length ? activeDepIds : undefined}
              limit={10}
            />
          </div>
        </div>
      </main>

      {/* Create Task Modal */}
      <Modal
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        title="Create Task"
      >
        <CreateTaskForm
          familyId={dashboard.family.id}
          dependents={dashboard.dependents}
          familyMembers={dashboard.familyMembers}
          onSuccess={() => {
            setShowCreateTask(false);
            loadDashboard();
          }}
          onCancel={() => setShowCreateTask(false)}
        />
      </Modal>

      {/* Create Medication Modal */}
      <Modal
        isOpen={showCreateMedication}
        onClose={() => setShowCreateMedication(false)}
        title="Create Medication"
      >
        <CreateMedicationForm
          familyId={dashboard.family.id}
          dependentId={primaryDepId || ''}
          familyMembers={dashboard.familyMembers}
          onSuccess={() => {
            setShowCreateMedication(false);
            loadDashboard();
          }}
          onCancel={() => setShowCreateMedication(false)}
        />
      </Modal>

      {/* Create Appointment Modal */}
      <Modal
        isOpen={showCreateAppointment}
        onClose={() => setShowCreateAppointment(false)}
        title="Create Appointment"
      >
        <CreateAppointmentForm
          familyId={dashboard.family.id}
          dependents={dashboard.dependents}
          familyMembers={dashboard.familyMembers}
          onSuccess={() => {
            setShowCreateAppointment(false);
            loadDashboard();
          }}
          onCancel={() => setShowCreateAppointment(false)}
        />
      </Modal>

      {/* Custody Setup Modal */}
      <Modal
        isOpen={showCustodySetup}
        onClose={() => setShowCustodySetup(false)}
        title="Custody Schedule"
      >
        <CustodySetup
          familyId={dashboard.family.id}
          dependentId={primaryDepId || ''}
          familyMembers={dashboard.familyMembers}
          onSuccess={() => {
            setShowCustodySetup(false);
            loadDashboard();
          }}
          onCancel={() => setShowCustodySetup(false)}
        />
      </Modal>

      {/* Create Activity Modal */}
      <Modal
        isOpen={showCreateActivity}
        onClose={() => setShowCreateActivity(false)}
        title="Add Activity"
      >
        <ActivityForm
          familyId={dashboard.family.id}
          dependents={dashboard.dependents}
          familyMembers={dashboard.familyMembers}
          onSuccess={() => {
            setShowCreateActivity(false);
            loadDashboard();
          }}
          onCancel={() => setShowCreateActivity(false)}
        />
      </Modal>

      {/* Create Diary Entry Modal */}
      <Modal
        isOpen={showCreateDiaryEntry}
        onClose={() => setShowCreateDiaryEntry(false)}
        title="Add Diary Entry"
      >
        <CreateDiaryEntryForm
          familyId={dashboard.family.id}
          dependents={dashboard.dependents}
          onSuccess={() => {
            setShowCreateDiaryEntry(false);
            setActivityKey((k) => k + 1);
          }}
          onCancel={() => setShowCreateDiaryEntry(false)}
        />
      </Modal>

    </div>
  );
}
